/**
 * Immersive Theme System Types
 * 
 * Tipos para o sistema de temas imersivos que afetam todos os componentes da aplicação.
 */

// ============================================
// ENUMS
// ============================================

export enum ImmersiveThemeType {
  NONE = 'NONE',
  NEUMORPHISM = 'NEUMORPHISM',
  BRUTALIST = 'BRUTALIST',
  RETRO_TERMINAL = 'RETRO_TERMINAL',
  ORGANIC_NATURE = 'ORGANIC_NATURE',
  LUXURY_GOLD = 'LUXURY_GOLD',
}

// ============================================
// CONFIGURAÇÕES ESPECÍFICAS DE CADA TEMA
// ============================================

export interface NeumorphismConfig {
  baseColor?: string;
  shadowIntensity?: 'light' | 'medium' | 'strong';
  borderRadius?: number;
}

export interface BrutalistConfig {
  accentColor?: 'red' | 'yellow' | 'blue' | 'black';
  borderWidth?: number;
  useUppercase?: boolean;
}

export interface RetroTerminalConfig {
  colorScheme?: 'green' | 'amber' | 'cyan' | 'white';
  scanlineOpacity?: number;
  cursorStyle?: 'block' | 'underline' | 'bar';
  glowEnabled?: boolean;
}

export interface OrganicNatureConfig {
  palette?: 'forest' | 'desert' | 'ocean' | 'meadow';
  textureEnabled?: boolean;
  animationSpeed?: 'slow' | 'normal' | 'fast';
  useNatureIcons?: boolean;
}

export interface LuxuryGoldConfig {
  metalType?: 'gold' | 'silver' | 'rose-gold' | 'platinum';
  shimmerEnabled?: boolean;
  fontStyle?: 'classic' | 'modern' | 'elegant';
  goldBorders?: boolean;
}

export type ThemeConfig = 
  | NeumorphismConfig 
  | BrutalistConfig 
  | RetroTerminalConfig 
  | OrganicNatureConfig 
  | LuxuryGoldConfig;

// ============================================
// DTOs
// ============================================

export interface UpdateImmersiveThemeDto {
  themeType: ImmersiveThemeType;
  config?: ThemeConfig;
  colorVariant?: string;
  reducedMotion?: boolean;
  highContrast?: boolean;
}

export interface ImmersiveThemeResponse {
  id: string;
  tenantId: string;
  themeType: ImmersiveThemeType;
  config?: Record<string, unknown>;
  colorVariant?: string;
  reducedMotion: boolean;
  highContrast: boolean;
  isPreviewMode: boolean;
  cssVariables: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface ThemePreset {
  type: ImmersiveThemeType;
  name: string;
  description: string;
  thumbnailUrl: string;
  features: string[];
  colorVariants: string[];
  defaultCssVariables: Record<string, string>;
}

export interface AllThemePresetsResponse {
  presets: ThemePreset[];
}

// ============================================
// CONTEXT
// ============================================

export interface ImmersiveThemeContextValue {
  // Estado atual
  theme: ImmersiveThemeType;
  config: ThemeConfig | null;
  colorVariant: string | null;
  cssVariables: Record<string, string>;
  
  // Acessibilidade
  reducedMotion: boolean;
  highContrast: boolean;
  
  // Estado de carregamento
  isLoading: boolean;
  isPreviewMode: boolean;
  
  // Ações
  setTheme: (theme: ImmersiveThemeType, config?: ThemeConfig) => Promise<void>;
  updateConfig: (config: ThemeConfig) => Promise<void>;
  setColorVariant: (variant: string) => Promise<void>;
  setAccessibility: (reducedMotion: boolean, highContrast: boolean) => Promise<void>;
  previewTheme: (theme: ImmersiveThemeType, config?: ThemeConfig) => Promise<void>;
  resetTheme: () => Promise<void>;
  applyPreview: () => Promise<void>;
  cancelPreview: () => void;
  
  // Presets
  presets: ThemePreset[];
  getPreset: (type: ImmersiveThemeType) => ThemePreset | undefined;
}

// ============================================
// PROPS DOS COMPONENTES TEMÁTICOS
// ============================================

export interface ThemedComponentProps {
  className?: string;
  children?: React.ReactNode;
}

export interface ThemedInputProps extends ThemedComponentProps {
  type?: 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search';
  placeholder?: string;
  value?: string;
  defaultValue?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  label?: string;
  helperText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  name?: string;
  id?: string;
  autoComplete?: string;
  autoFocus?: boolean;
  required?: boolean;
  maxLength?: number;
  minLength?: number;
  pattern?: string;
}

export interface ThemedButtonProps extends ThemedComponentProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  form?: string;
}

export interface ThemedSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
}

export interface ThemedSelectProps extends ThemedComponentProps {
  options: ThemedSelectOption[];
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  label?: string;
  helperText?: string;
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  multiple?: boolean;
  name?: string;
  id?: string;
  required?: boolean;
}

export interface ThemedTableColumn {
  key: string;
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  sortable?: boolean;
  render?: (value: unknown, row: Record<string, unknown>, index: number) => React.ReactNode;
}

export interface ThemedTableProps extends ThemedComponentProps {
  columns: ThemedTableColumn[];
  data: Array<Record<string, unknown>>;
  onRowClick?: (row: Record<string, unknown>, index: number) => void;
  loading?: boolean;
  emptyMessage?: string;
  striped?: boolean;
  hoverable?: boolean;
  bordered?: boolean;
  compact?: boolean;
  stickyHeader?: boolean;
  maxHeight?: string;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
}

export interface ThemedModalProps extends ThemedComponentProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  footer?: React.ReactNode;
  centered?: boolean;
}

export interface ThemedCardProps extends ThemedComponentProps {
  title?: string;
  subtitle?: string;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  hoverable?: boolean;
  bordered?: boolean;
  elevated?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

export interface ThemedTabItem {
  id: string;
  label: string;
  content: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  badge?: string | number;
}

export interface ThemedTabsProps extends ThemedComponentProps {
  tabs: ThemedTabItem[];
  defaultTab?: string;
  activeTab?: string;
  onChange?: (tabId: string) => void;
  variant?: 'line' | 'enclosed' | 'pills';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  centered?: boolean;
}

export interface ThemedBadgeProps extends ThemedComponentProps {
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info' | 'primary';
  size?: 'sm' | 'md' | 'lg';
  rounded?: boolean;
  dot?: boolean;
  outline?: boolean;
}

export interface ThemedTooltipProps extends ThemedComponentProps {
  content: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  delay?: number;
  disabled?: boolean;
  arrow?: boolean;
  maxWidth?: string;
}

// ============================================
// THEME METADATA
// ============================================

export interface ThemeMetadata {
  name: string;
  description: string;
  icon: string;
  colorScheme: 'light' | 'dark' | 'both';
  tags: string[];
}

export const THEME_METADATA: Record<ImmersiveThemeType, ThemeMetadata> = {
  [ImmersiveThemeType.NONE]: {
    name: 'Padrão',
    description: 'Estilo padrão da aplicação',
    icon: '🎨',
    colorScheme: 'both',
    tags: ['default', 'clean', 'minimal'],
  },
  [ImmersiveThemeType.NEUMORPHISM]: {
    name: 'Neumorfismo',
    description: 'Soft UI com sombras suaves e efeito tátil',
    icon: '🌫️',
    colorScheme: 'both',
    tags: ['soft', 'modern', '3D', 'shadows'],
  },
  [ImmersiveThemeType.BRUTALIST]: {
    name: 'Brutalista',
    description: 'Design cru com bordas grossas e tipografia bold',
    icon: '🏗️',
    colorScheme: 'both',
    tags: ['bold', 'raw', 'typography', 'contrast'],
  },
  [ImmersiveThemeType.RETRO_TERMINAL]: {
    name: 'Terminal Retrô',
    description: 'Estética de terminais DOS/Unix dos anos 80',
    icon: '💻',
    colorScheme: 'dark',
    tags: ['retro', 'hacker', 'monospace', 'glow'],
  },
  [ImmersiveThemeType.ORGANIC_NATURE]: {
    name: 'Natureza Orgânica',
    description: 'Formas orgânicas com cores terrosas',
    icon: '🌿',
    colorScheme: 'light',
    tags: ['organic', 'natural', 'earthy', 'calm'],
  },
  [ImmersiveThemeType.LUXURY_GOLD]: {
    name: 'Luxo Dourado',
    description: 'Premium com gradientes dourados e elegância',
    icon: '✨',
    colorScheme: 'dark',
    tags: ['luxury', 'premium', 'gold', 'elegant'],
  },
};
